import {
  AuthenticationServiceProvider,
  OktaMetaData,
} from 'module/api/account/types';
import { Company } from 'module/api/company/types';
import { LoginWidgetLinks } from 'module/authentication/types';
import { LinkText, UserInfo } from 'module/ui/components/atoms/form-elements';
import {
  BackgroundColor,
  InputBorder,
  InputFocus,
} from 'module/ui/components/atoms/input/input';
import { Layout } from 'module/ui/components/atoms/layout';
import {
  Label01,
  ProductiveHeading04,
  Short01,
  Text01,
} from 'module/ui/components/styles';
import { useOktaAuth } from '@okta/okta-react';
import { Divider } from 'antd';
import { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import './login.css';
import { useCognitoAuth } from '../cognito-sign-in-widget/use-cognito-auth';
import { CognitoSignInWidget } from '../cognito-sign-in-widget/widget';
import { CompanyLogo } from '../company-logo/logo';
import { ForgotPasswordLink } from '../forgot-password-link';
import { OktaSignInWidget } from '../okta-sign-in-widget/widget';
import { SsoSamlButton } from '../saml-button/button';

const { Content } = Layout;

export const Container = styled(Content)`
  display: flex;
  flex-direction: column;
  height: 100vh;
  padding: 88px 120px;
  align-items: flex-start;
`;

export const WelcomeHeading = styled.strong`
  ${ProductiveHeading04};
  font-weight: var(--heading-8-weight);
`;

export const Heading = styled.div`
  ${ProductiveHeading04};
  font-size: var(--heading-5-size);
`;

const HelperLink = styled(Link)`
  ${Short01};
`;

export const HeadingContainer = styled.div`
  display: flex;
  gap: var(--spacing-05);
  align-items: center;
  margin-bottom: var(--layout-05);
`;

export const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 400px;
  & button {
    height: 50px;
  }
`;

const LinksContainer = styled.div`
  display: flex;
  gap: var(--spacing-03);
  align-items: center;
`;

const StyledDivider = styled(Divider)`
  span {
    transform: translateY(50%);
    font-size: var(--font-size-sm);
    color: var(--gray-50);
  }
`;

const WidgetContainer = styled.div`
  width: fit-content;
  position: relative;
`;

export const HelperTitle = styled.div`
  margin-bottom: var(--spacing-04);
  font-size: var(--font-size-lg);
  font-weight: var(--heading-6-weight);
`;

const StyledSignInWidget = styled(OktaSignInWidget)`
  #okta-sign-in {
    margin-bottom: 0 !important;
    &.auth-container {
      &.main-container {
        box-shadow: none;
        border: none;
        margin-top: 0;
      }
      * {
        font-family: var(--font-family) !important;
      }
    }
    .auth-content {
      padding: 0;
    }
    .auth-footer {
      display: none;
    }
    label {
      ${Label01};
    }

    #okta-sign-in .o-form .input-fix {
      background: var(--gray-10) !important;
    }
    .o-form-fieldset {
      margin-bottom: var(--spacing-05) !important;

      :nth-child(1) {
        margin-bottom: 0px !important;
      }
      :nth-child(2) {
        margin-bottom: var(--spacing-03) !important;
      }
    }

    .o-form-content {
      padding-bottom: 0 !important;
    }

    .o-form-button-bar {
      padding-bottom: var(--spacing-03);
    }

    .o-form .okta-form-input-field {
      width: 100%;
      padding: 0 var(--spacing-05);
      ${InputBorder};
      ${Short01};
      ${Text01};
      ${BackgroundColor};
      height: var(--input-height-lg);

      &.focused-input {
        ${InputFocus};
      }
    }

    /* For signin button*/
    #okta-signin-submit.button.button-primary {
      text-align: left;
      border-radius: var(--border-radius-small);
      padding: 0 var(--spacing-03);
      background-color: var(--button-primary);
      border-color: var(--button-primary);
      color: var(--text-on-color);
    }
  }

  .custom-checkbox {
    color: var(--gray-80) !important;
  }

  #okta-sign-in .focused-input {
    box-shadow: none;
  }
  #okta-sign-in .social-auth-button {
    padding: 0 0 0 var(--spacing-05);
    background: var(--blue-60);
    color: var(--white-100);
  }
`;

interface Props {
  oktaMetaData?: OktaMetaData;
  isSamlMandatory: boolean;
  links: LoginWidgetLinks;
  company: Company;
}

export const LoginWithCredentials: FC<React.PropsWithChildren<Props>> = ({
  oktaMetaData,
  isSamlMandatory,
  company,
  links,
}) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const customerParam = params.get('customer');
  const partnerParam = params.get('partner');

  const isSamlEnabled = useMemo(
    () => !!(oktaMetaData?.idps ?? []).filter((idp) => idp.enabled).length,
    [oktaMetaData?.idps],
  );

  const isCognitoEnabled =
    oktaMetaData?.authenticationServiceProvider ===
    AuthenticationServiceProvider.COGNITO;

  const { samlFlowHandler, googleSSOHandler } = useCognitoAuth({
    oktaMetaData: oktaMetaData,
  });

  const { oktaAuth } = useOktaAuth();

  return oktaMetaData ? (
    <Layout>
      {customerParam ? (
        <></>
      ) : (
        <Container>
          <HeadingContainer>
            <CompanyLogo data={company} />
            <TextContainer>
              <WelcomeHeading>
                <FormattedMessage id="welcome" />
              </WelcomeHeading>
              <Heading>
                <FormattedMessage
                  id="login.title"
                  values={{ companyName: company.companyName }}
                />
              </Heading>
            </TextContainer>
          </HeadingContainer>

          {!customerParam &&
            !partnerParam &&
            isSamlEnabled &&
            !isSamlMandatory && (
              <ButtonContainer>
                {(oktaMetaData.idps ?? [])
                  .filter((idp) => idp.enabled)
                  .map((idp) => {
                    return (
                      <SsoSamlButton
                        key={idp.idpId}
                        idp={idp}
                        googleSSOHandler={googleSSOHandler}
                        samlFlowHandler={samlFlowHandler}
                        isCognitoEnabled={isCognitoEnabled}
                        oktaAuth={oktaAuth}
                      />
                    );
                  })}

                <StyledDivider orientation="center">
                  <FormattedMessage id="sign_in_with_your_email" />
                </StyledDivider>
              </ButtonContainer>
            )}

          {partnerParam && (
            <HelperTitle>
              <FormattedMessage id="signing_in_as_partner" />
            </HelperTitle>
          )}

          {!customerParam &&
            (isCognitoEnabled ? (
              <CognitoSignInWidget
                isSamlMandatory={isSamlMandatory}
                oktaMetaData={oktaMetaData}
                links={links}
              />
            ) : (
              <WidgetContainer>
                <StyledSignInWidget
                  oktaMetaData={oktaMetaData}
                  isSamlMandatory={isSamlMandatory}
                  isPartner={partnerParam != null}
                  links={links}
                />

                <ForgotPasswordLink
                  isSamlMandate={isSamlMandatory}
                  forgotPasswordLink={links.forgotPasswordUrl}
                />
              </WidgetContainer>
            ))}

          {!customerParam && !partnerParam && (
            <LinksContainer>
              <HelperLink to={`${links.loginUrl}?customer=true`}>
                <FormattedMessage
                  id="are_you_a_customer"
                  values={{ companyName: company.companyName }}
                />
              </HelperLink>{' '}
              {isSamlMandatory && (
                <>
                  /{' '}
                  <HelperLink to={`${links.loginUrl}?partner=true`}>
                    <FormattedMessage
                      id="are_you_a_partner"
                      values={{ companyName: company.companyName }}
                    />
                  </HelperLink>
                </>
              )}
            </LinksContainer>
          )}

          {customerParam || partnerParam ? (
            <UserInfo>
              <FormattedMessage
                id="forgot_password.cta_back"
                values={{
                  a: (chunks) => (
                    <Link to={links.loginUrl}>
                      <LinkText>{chunks}</LinkText>
                    </Link>
                  ),
                }}
              />
            </UserInfo>
          ) : (
            <></>
          )}
        </Container>
      )}
    </Layout>
  ) : (
    <></>
  );
};
